import {
  DataTable,
  FGCustomInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  IDataTableColumn,
  useGridState,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { CompetitionApi, DrupalCompetitionDto, DrupalJudgeDto } from "../../../../api";
import { EmailButton, FGSRSHDateMaskInput, PageContainer } from "../../../../components";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { CompetitionResultList } from "../../../../pages/sport/competition/item/CompetitionResultList";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalCompetitionItemPageProps {}

export const DrupalCompetitionItemPage: React.FunctionComponent<IDrupalCompetitionItemPageProps> = () => {
  const { id } = useParams<{ id: string }>();

  const competitionId = React.useMemo(() => +id, [id]);
  const { t } = useTl();
  const history = useHistory();

  const api = useApiService(CompetitionApi);

  const [clubs, cloading] = useReferential((a) => a.getClubs(null), true, []);
  //===================== Getting Data ===========================

  const { data, loading, saving } = useCrudApi<DrupalCompetitionDto>(
    React.useMemo(
      () => ({
        getApiFn: (apiId = competitionId) => api.getDrupalCompetition(apiId),
        saveApiFn: () => () => {},
        deleteApiFn: () => {},
      }),
      [api, competitionId]
    ),
    [competitionId]
  );

  const tableState = useGridState({
    serverMode: false,
    enablePagination: false,
  });

  React.useEffect(() => {
    if (!loading && tableState.data !== data?.judges) {
      tableState.setData(data?.judges);
    }
  }, [data, loading, tableState]);

  const columns = React.useMemo<IDataTableColumn<DrupalJudgeDto>[]>(
    () => [
      {
        fieldName: "computedName",
        header: () => t(ETLCodes.Name),
      },
    ],
    [t]
  );

  const onCancel = React.useCallback(() => history.push(`${ERoutes.competition}`), [history]);

  //============================= Form ==========================
  return (
    <PageContainer>
      <DrupalFormGenerator
        initialValues={data}
        editMode={false}
        onSubmit={() => {}}
        loading={loading}
        saving={saving}
        hideButtons={true}
        onCancel={onCancel}
      >
        <DrupalFieldSet title={t(ETLCodes.Competition)}>
          <FieldGroup columns={2}>
            <FGCustomInput name="personEmail" label={t(ETLCodes.ContactPerson)}>
              {(ctx) => <EmailButton email={data?.clubEmail} />}
            </FGCustomInput>
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSelectInput name="clubId" label={t(ETLCodes.Club)} items={clubs} loading={cloading} readonly />
          </FieldGroup>

          <FieldGroup columns={3}>
            <FGSRSHDateMaskInput name="dateFrom" label={t(ETLCodes.DateFrom)} requiredMark />
            <FGSRSHDateMaskInput name="dateTo" label={t(ETLCodes.DateTo)} requiredMark />
          </FieldGroup>
          <FieldGroup>
            <FGTextInput name="subActivityName" label={t(ETLCodes.SubActivity)} requiredMark />
            <FGTextInput name={data?.otherAddress ? "otherAddressString" : "clubAddress"} label={t(ETLCodes.Address)} />
          </FieldGroup>
        </DrupalFieldSet>
      </DrupalFormGenerator>

      <FieldSet title={t(ETLCodes.Judges)}>
        <DataTable tableState={tableState} columns={columns} loading={loading} />
      </FieldSet>

      <CompetitionResultList
        competitionId={competitionId}
        editMode={false}
        competitionRequestId={data?.competitionRequestId}
        hideExportButton={true}
      />
    </PageContainer>
  );
};
